
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Phone } from "lucide-react";

const WHATSAPP_LOGO_URL = "https://kxrduwrurqjvouiqobyc.supabase.co/storage/v1/object/public/photo-edm//WhatsApp.png";

interface ContactPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ContactPopup: React.FC<ContactPopupProps> = ({ open, onOpenChange }) => {
  const { data: admins } = useQuery({
    queryKey: ['contact-admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('display_on_contact', true);

      if (error) throw error;
      return data;
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] md:max-w-[520px]">
        <div className="flex flex-col items-center text-center gap-3">
          <h2 className="text-2xl font-playfair text-villa-blue mb-2">
            Une question ? Nous sommes là pour vous aider !
          </h2>
          <p className="text-gray-700 mb-2 text-base">
            Contactez-nous directement si vous avez la moindre hésitation ou besoin d'un conseil concernant votre réservation.
          </p>
          <div className="w-full flex flex-col gap-4 items-center">
            {admins?.map((admin) => (
              <div
                key={admin.id}
                className="w-full bg-villa-sand/40 p-3 rounded flex items-center gap-3 shadow transition duration-150"
              >
                <Avatar className="w-12 h-12 shrink-0">
                  <AvatarImage
                    src={
                      admin.profile_image
                        ? supabase.storage
                            .from("profile_images")
                            .getPublicUrl(admin.profile_image)
                            .data.publicUrl
                        : undefined
                    }
                  />
                  <AvatarFallback>
                    {admin.first_name?.[0]}
                    {admin.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <div className="font-semibold truncate">
                    {admin.first_name} {admin.last_name}
                  </div>
                  {admin.phone && (
                    <div className="flex items-center text-villa-turquoise mt-1">
                      <Phone className="w-4 h-4 mr-1" />
                      <a
                        href={`tel:${admin.phone}`}
                        className="hover:underline mr-2"
                      >
                        {admin.phone}
                      </a>
                      {admin.whatsapp_booking_url && (
                        <a
                          href={admin.whatsapp_booking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2"
                          title={`Contacter ${admin.first_name} sur WhatsApp`}
                        >
                          <img
                            src={WHATSAPP_LOGO_URL}
                            alt="WhatsApp"
                            className="w-6 h-6 object-contain inline-block"
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <hr className="my-2 w-full border-gray-300" />
          <div>
            <p className="text-sm text-gray-800 font-medium">
              Vous pouvez aussi nous écrire :
            </p>
            <a
              href="mailto:contact@villadoucevague.fr"
              className="text-villa-turquoise underline break-all"
            >
              contact@villadoucevague.fr
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactPopup;
